import { useState } from "react";
import { Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { useInfo } from "@/context/InfoContext";
import { inputCls, primaryBtn } from "@/pages/admin/AdminServices";

const resize = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const max = 1400;
      const k = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * k);
      c.height = Math.round(img.height * k);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

export default function AdminGallery() {
  const api = useApi();
  const { info, reload } = useInfo();
  const [url, setUrl] = useState("");
  const [alt, setAlt] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async (src) => {
    setBusy(true);
    try {
      await api("post", "/admin/gallery", { url: src, alt });
      setUrl(""); setAlt("");
      toast.success("Foto añadida");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("El archivo debe ser una imagen");
    try { await add(await resize(file)); } catch { toast.error("No se pudo leer la imagen"); }
  };

  const del = async (g) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    try { await api("delete", `/admin/gallery/${g.id}`); toast.success("Foto eliminada"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="border border-zinc-200 p-6 mb-8 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <label className="md:col-span-4 text-xs text-zinc-500">Descripción de la foto<input className={`${inputCls} w-full mt-1`} value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="Ej.: Degradado bajo con barba" data-testid="admin-gallery-alt" /></label>
        <label className="md:col-span-4 text-xs text-zinc-500">Enlace https de la imagen<input className={`${inputCls} w-full mt-1`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid="admin-gallery-url" /></label>
        <button disabled={busy || !url.startsWith("https://")} onClick={() => add(url)} className={`${primaryBtn} md:col-span-2 justify-center`} data-testid="admin-gallery-add-url">Añadir enlace</button>
        <label className={`${primaryBtn} md:col-span-2 justify-center cursor-pointer`} data-testid="admin-gallery-upload-label">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir foto
          <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="admin-gallery-file" />
        </label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(info?.gallery || []).map((g, i) => (
          <div key={g.id} className="relative group" data-testid={`admin-gallery-item-${i}`}>
            <img src={g.url} alt={g.alt} className="w-full aspect-[3/4] object-cover bg-zinc-100" />
            <p className="text-xs text-zinc-500 mt-2 truncate">{g.alt || "Sin descripción"}</p>
            <button onClick={() => del(g)} className="absolute top-2 right-2 w-9 h-9 bg-white border border-zinc-200 flex items-center justify-center hover:border-red-500" aria-label="Eliminar" data-testid={`admin-gallery-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
